import { Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';

import { NavigationRoutingStructure } from '~/scenes/PortalViews/components/StructuredModuleRouter/types';
import { browserLogError } from '~/utils/helper';

type PropsType = {
  routingStructure?: NavigationRoutingStructure[];
};

type LevelPropsType = {
  routeItem: NavigationRoutingStructure;
  parentPath: string;
  depth: number;
};

const StructuredModuleRouterLevel = (props: LevelPropsType) => {
  const { routeItem, parentPath, depth } = props;
  const { subPaths, component, path } = routeItem;

  const { t } = useTranslation();

  const routeItemPath = depth === 0 ? parentPath : `${parentPath}/${path}`;
  const nextDepth = depth + 1;
  const hasSubPaths = !!subPaths?.length;
  const hasView = !!component;

  const renderRoutingError = () => {
    browserLogError(
      `StructuredModuleRouter: Structure configuration issue when rendering ${routeItemPath}`,
    );
    return <Alert severity={'error'}>{t('laGeneralError')}</Alert>;
  };

  if (!hasSubPaths && !hasView) {
    return renderRoutingError();
  }

  if (!hasSubPaths && hasView) {
    return <>{component}</>;
  }

  const validSubPaths = subPaths?.map((s) => s.path) ?? [];

  const defaultRedirectPath = !!validSubPaths[0]
    ? `${routeItemPath}/${validSubPaths[0]}`
    : undefined;

  const renderExactPath = () => {
    if (!!component) {
      return component;
    } else if (defaultRedirectPath) {
      return <Redirect to={defaultRedirectPath} />;
    } else {
      return renderRoutingError();
    }
  };

  const renderInvalidPath = () => {
    if (defaultRedirectPath) {
      return <Redirect to={defaultRedirectPath} />;
    } else {
      return renderRoutingError();
    }
  };

  return (
    <Switch>
      <Route path={routeItemPath} exact={true}>
        {renderExactPath()}
      </Route>
      {subPaths?.map((subItem) => {
        return (
          <Route
            key={`${subItem.path}-${nextDepth}`}
            path={`${routeItemPath}/${subItem.path}`}
          >
            <StructuredModuleRouterLevel
              routeItem={subItem}
              depth={nextDepth}
              parentPath={routeItemPath}
            />
          </Route>
        );
      })}
      <Route>{renderInvalidPath()}</Route>
    </Switch>
  );
};

export const StructuredModuleRouter = (props: PropsType) => {
  const { routingStructure } = props;

  const { path } = useRouteMatch();

  if (!routingStructure || !routingStructure.length) {
    return <></>;
  }

  const baseItem: NavigationRoutingStructure = {
    label: '',
    path: '',
    subPaths: routingStructure,
  };

  return (
    <StructuredModuleRouterLevel
      routeItem={baseItem}
      parentPath={path}
      depth={0}
    />
  );
};
